import { useContext, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Tooltip,
  Card,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import SearchBar from '../../../common/search-bar/search-bar';

import FilterBar from '../../../common/filter-bar/filter-bar';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { useImmer } from 'use-immer';
import { DoubleDate } from '../../../common/double-date-picker/double-date-picker.interfaces';
import DoubleDatePicker from '../../../common/double-date-picker/double-date-picker';
import BulkAction from '../../../common/bulk-action/bulk-action';
import { OrderType } from '../../../../interfaces/delivery-company/orders';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import axios from 'axios';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { buildQueryParams } from '../../../common/filter-bar/filter-bar.utils';

import { PaginationContext } from '../../../../contexts/pagination-context';
import { OrgSettingsContext } from '../../../../contexts/org-settings-context';
import { IFilterOption } from '../../../common/filter-bar/types';
import { ShopLocationType } from '../../../../interfaces/delivery-company/pickups';
import GenericTable from '../../../common/generic-table/generic-table';
import { useSnackBar } from '../../../../contexts/snackbar';
import { displayLocation } from '../../../../utils/locations';
import { IsOpenCell, ModeCell } from '../../utils/mode';

function StatusDot({
                     isOpen,
                     reason,
                     size = 10,
                   }: {
  isOpen?: boolean;
  reason?: string;
  size?: number;
}) {
  if (typeof isOpen !== 'boolean') return null;
  const title = reason ?? (isOpen ? 'Open now' : 'Closed now');
  return (
    <Tooltip title={title}>
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          bgcolor: isOpen ? 'success.main' : 'error.main',
          boxShadow: 1,
          display: 'inline-block',
          verticalAlign: 'middle',
        }}
      />
    </Tooltip>
  );
}


function MerchantPickupsListing() {
  const [pickups, setPickups] = useState<ShopLocationType[]>([]);
  const [pickupsCount, setPickupsCount] = useState(0);
  const [rowLoading, setRowLoading] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { showMessage } = useSnackBar();

  const { user, config } = useContext(AuthContext);

  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: paginationConfig.rowsPerPage,
  });

  const fetchPickups = async () => {
    setIsLoading(true);
    const endpoint = `${API_ENDPOINT}/api/v1/pickups/?page=${controller.page + 1}&page_size=${controller.rowsPerPage}}`;
    try {
      console.log(endpoint);
      const response = await axios.get<{
        count: number;
        results: ShopLocationType[];
      }>(endpoint, config);
      setPickups(response.data.results);
      setPickupsCount(response.data.count);
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchPickups();
  }, [controller]);

  const setRowLoadingFlag = (id: string, v: boolean) =>
    setRowLoading((prev) => ({ ...prev, [id]: v }));

  const updatePickupLocal = (updated: ShopLocationType) =>
    setPickups((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));

  const handleChangeMode = async (
    pickup: ShopLocationType,
    next: 'OPEN' | 'CLOSED' | 'SCHEDULE',
  ) => {
    if (pickup.mode === next) return;
    const prev = pickup.mode;
    // optimistic local flip (and optional is_open tweak for UX)
    const optimistic: ShopLocationType = {
      ...pickup,
      mode: next,
      is_open:
        next === 'OPEN' ? true : next === 'CLOSED' ? false : pickup.is_open,
    };
    updatePickupLocal(optimistic);
    setRowLoadingFlag(pickup.id, true);

    try {
      const endpoint = `${API_ENDPOINT}/api/v1/pickup/${pickup.id}/`;
      const res = await axios.patch(endpoint, { mode: next }, config);
      // prefer server object if returned
      if (res?.data?.id === pickup.id) updatePickupLocal(res.data);
    } catch (e) {
      // revert on error
      updatePickupLocal({ ...pickup, mode: prev });
      showMessage('Failed to update mode', 'error');
    } finally {
      setRowLoadingFlag(pickup.id, false);
    }
  };
  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationConfig({ ...paginationConfig, rowsPerPage: newRowsPerPage });
    setController({
      ...controller,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  const searchQueryAction = (
    column: string,
    value: string,
    operation: string,
  ) => {
    setController({
      ...controller,
      page: 0,
    });
  };

  const resetPaginationControls = () => {
    setController({
      ...controller,
      page: 0,
    });
  };

  const resetAll = () => {
    resetPaginationControls();
  };

  const handleMakeLocationDefault = async (pickupId: string) => {
    setIsLoading(true);
    const requestData = {
      is_default: true,
    };
    try {
      const endpoint = `${API_ENDPOINT}/api/v1/pickup/${pickupId}/`;
      await axios.patch(endpoint, requestData, config);
      showMessage('Updated default location');
      const new_pickups = pickups.map((item) => {
        return { ...item, is_default: item.id == pickupId };
      });
      setPickups(new_pickups);
    } catch (e: any) {
      showMessage('Failed to update default shop location', 'error');
    }
    setIsLoading(false);
  };

  return (
    <Box>
      <GenericTable
        sourceView="merchant-pickup-listing"
        items={pickups}
        selectedItems={new Map<string, ShopLocationType>()}
        isLoading={isLoading}
        getItemUID={(pickup) => pickup.id}
        fullWidth={true}
        columns={[
          {
            id: 'id',
            header: <p>ID</p>,
            display_body: (pickup) => (
              <a href={`/merchant-pickup/${pickup.id}`}>
                <b>{pickup.id}</b>
              </a>
            ),
          },
          {
            id: 'name',
            header: <p>Name</p>,
            display_body: (pickup) => (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <StatusDot
        isOpen={pickup.is_open}
        reason={
          pickup.mode === 'OPEN'
            ? 'Forced open'
            : pickup.mode === 'CLOSED'
              ? 'Forced closed'
              : 'Following schedule'
        }
      />
      <a href={`/merchant-pickup/${pickup.id}`}>
        <b>{pickup.name}</b>
      </a>
    </span>
            ),
          },
          {
            id: 'make_default',
            header: 'Default',
            display_body: (pickup: ShopLocationType) =>
              pickup.is_default ? (
                <span style={{ color: 'green', fontWeight: 'bold' }}>
                  ✅ Default
                </span>
              ) : (
                <Button
                  color="primary"
                  size="small"
                  onClick={() => handleMakeLocationDefault(pickup.id)}
                >
                  Set as Default
                </Button>
              ),
          },
          {
            id: 'mode',
            header: <p>Actions</p>,
            display_body: (pickup: ShopLocationType) => (
              <ModeCell
                pickup={pickup}
                loading={!!rowLoading[pickup.id]}
                onModeChange={(next) => handleChangeMode(pickup, next)}
                onScheduleSubmit={() => {
                  fetchPickups();
                }}
              />
            ),
          },
          {
            id: 'address',
            header: <p>Address</p>,
            display_body: (pickup) => <p>{displayLocation(pickup)}</p>,
          },
          {
            id: 'contact_person',
            header: <p>Contact Person</p>,

            display_body: (pickup) => <p>{pickup.contact_person} </p>,
          },
          {
            id: 'contact_phone',
            header: <p>Contact Phone</p>,
            display_body: (pickup) => <p> {pickup.contact_phone}</p>,
          },
        ]}
        updateSelectedItems={function(
          updater: (draft: Map<string, ShopLocationType>) => void,
        ): void {
          throw new Error('Function not implemented.');
        }}
      />

      <TablePagination
        component="div"
        onPageChange={handlePageChange}
        page={controller.page}
        count={pickupsCount}
        rowsPerPage={controller.rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
        labelRowsPerPage=""
        style={{ overflow: 'visible' }}
      />
    </Box>
  );
}

export default MerchantPickupsListing;
